import { useEffect, useMemo, useState } from 'react'
import { fetchAcquisition, type AcquisitionResponse, type AcquisitionRow } from '../../lib/api'
import { useFlowFilter } from '../FlowFilterContext'

type SortKey = 'sessions' | 'leads' | 'conversionRate'

interface Props { range: { from: number; to: number } }

const fmt = (n: number) => n.toLocaleString('da-DK')
const fmtPct = (p: number) => `${(p * 100).toFixed(1)}%`

export function AcquisitionTable({ range }: Props) {
  const { variantParam, audienceParam } = useFlowFilter()
  const [data, setData] = useState<AcquisitionResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [sortKey, setSortKey] = useState<SortKey>('sessions')

  useEffect(() => {
    let cancelled = false
    setData(null); setError(null)
    fetchAcquisition(range, variantParam, audienceParam)
      .then((d) => { if (!cancelled) setData(d) })
      .catch((e) => { if (!cancelled) setError(String(e)) })
    return () => { cancelled = true }
  }, [range.from, range.to, variantParam, audienceParam])

  const sorted = useMemo<AcquisitionRow[]>(() => {
    if (!data) return []
    return [...data.rows].sort((a, b) => b[sortKey] - a[sortKey])
  }, [data, sortKey])

  if (error) return <div className="card card-empty">Kunne ikke hente acquisition-data</div>
  if (!data) return <div className="card card-skeleton" aria-busy />
  if (sorted.length === 0) return (
    <div className="card card-empty">
      <div className="card-title">Acquisition</div>
      <div className="muted">Ingen sessioner i perioden</div>
    </div>
  )

  const benchmark = data.totals.conversionRate
  const topRow = sorted[0]

  return (
    <div className="card">
      <div className="card-header">
        <div className="card-title">Acquisition</div>
        <div className="card-subtitle">Sessioner og leads pr. trafikkilde</div>
      </div>
      <div className="acquisition-totals">
        <span><strong>{fmt(data.totals.sessions)}</strong> sessioner</span>
        <span className="dot">·</span>
        <span><strong>{fmt(data.totals.leads)}</strong> leads</span>
        <span className="dot">·</span>
        <span>Benchmark CR <strong>{fmtPct(benchmark)}</strong></span>
      </div>
      <div className="table-scroll">
        <table className="data-table">
          <thead>
            <tr>
              <th>Source</th>
              <th>Medium</th>
              <th>Campaign</th>
              <th className={`sortable ${sortKey === 'sessions' ? 'active' : ''}`} onClick={() => setSortKey('sessions')}>Sessioner</th>
              <th className={`sortable ${sortKey === 'leads' ? 'active' : ''}`} onClick={() => setSortKey('leads')}>Leads</th>
              <th className={`sortable ${sortKey === 'conversionRate' ? 'active' : ''}`} onClick={() => setSortKey('conversionRate')}>CR</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((r) => {
              const above = r.sessions >= 30 && r.conversionRate > benchmark * 1.15
              const below = r.sessions >= 30 && r.conversionRate < benchmark * 0.85
              return (
                <tr key={`${r.source}|${r.medium}|${r.campaign}`}>
                  <td>{r.source}</td>
                  <td className="muted">{r.medium}</td>
                  <td className="muted">{r.campaign}</td>
                  <td className="num">{fmt(r.sessions)}</td>
                  <td className="num">{fmt(r.leads)}</td>
                  <td className={`num ${above ? 'tone-up' : below ? 'tone-down' : ''}`}>
                    {r.sessions === 0 ? '—' : fmtPct(r.conversionRate)}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      {topRow.sessions >= 30 && (
        <div className="card-note">
          Bedste kilde: <strong>{topRow.source} / {topRow.medium}</strong> · {fmtPct(topRow.conversionRate)} CR
        </div>
      )}
    </div>
  )
}
